import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import { createContext, useRef } from 'react';
import { LANGUAGES } from '@crehana/ts-types';
import { toJsx } from "./formatters";
import { CrehanaI18n } from "./i18n";
import { pipe, validateIsConfig, validateIsFormatterConfig } from "./utils";
import { jsx as _jsx } from "react/jsx-runtime";
var noopTFunction = function noopTFunction(k, doc, conf) {
  if (typeof doc === 'string') return doc;
  if (validateIsConfig(doc) && doc.defaultValue) return doc.defaultValue;
  if (validateIsConfig(conf) && conf.defaultValue) return conf.defaultValue;
  return k;
};
export var CrehanaI18nContext = /*#__PURE__*/createContext({
  t: noopTFunction,
  addResourceBundle: function addResourceBundle() {
    return true;
  },
  languageCode: LANGUAGES.ES
});
function getFormatters(config) {
  var _config$formatters;
  var formatters = config.formatter ? [config.formatter] : [];
  if ((_config$formatters = config.formatters) !== null && _config$formatters !== void 0 && _config$formatters.length) {
    config.formatters.forEach(function (f) {
      if (f) {
        formatters.push(f);
      }
    });
  }
  return pipe.apply(void 0, formatters);
}

// All the keys accepted by the config
var KNOWN_KEYS = {
  defaultValue: true,
  namespace: true,
  interpolation: true,
  count: true,
  formatter: true,
  formatters: true
};

/**
 * Support old version of interpolation where unknown keys would be interpolated
 */
function parseExtraConfig(config) {
  var actualConfig = _objectSpread({}, config);

  // Get all the keys that are not part of the API
  var keys = Object.keys(config).filter(function (k) {
    return !KNOWN_KEYS[k];
  });
  if (keys.length) {
    var _actualConfig$interpo;
    actualConfig.interpolation = (_actualConfig$interpo = actualConfig.interpolation) !== null && _actualConfig$interpo !== void 0 ? _actualConfig$interpo : {};
    keys.forEach(function (k) {
      if (process.env.NODE_ENV === 'development') {
        if (config[k]) {
          console.warn('%c WARNING:\n', 'font-size: 18px', 'crehana-i18n no longer supports unknown keys as interpolation values', 'please update use the interpolation property in the config object', 'this was previously available as part of the migration from react-i18next.', "Found key: ".concat(k));
        }
      }
      actualConfig.interpolation[k] = config[k];
    });
  }
  return actualConfig;
}
export var I18nProvider = function I18nProvider(_ref) {
  var languageCode = _ref.languageCode,
    countryPrefix = _ref.countryPrefix,
    countryCode = _ref.countryCode,
    translations = _ref.translations,
    defaultTranslations = _ref.defaultTranslations,
    defaultNs = _ref.defaultNs,
    fallbackLanguageCode = _ref.fallbackLanguageCode,
    fallbackNs = _ref.fallbackNs,
    children = _ref.children;
  var i18nInstance = useRef(new CrehanaI18n({
    language: languageCode,
    namespace: defaultNs
  }));

  // @ts-expect-error is difficult to manage the intersection React.ReactNode | string
  var t = function t(key, defaultValueOrConfig, config) {
    if (typeof defaultValueOrConfig === 'string') {
      var conf = validateIsConfig(config) ? parseExtraConfig(config) : undefined;
      return i18nInstance.current.t(key, _objectSpread({
        defaultValue: defaultValueOrConfig
      }, conf));
    }
    var result = '';
    if (validateIsConfig(defaultValueOrConfig)) {
      result = i18nInstance.current.t(key, parseExtraConfig(defaultValueOrConfig));
    } else {
      result = i18nInstance.current.t(key);
    }
    if (validateIsFormatterConfig(defaultValueOrConfig)) {
      var formatter = getFormatters(defaultValueOrConfig);
      return toJsx(formatter(result));
    }
    return result;
  };
  var addResourceBundle = function addResourceBundle(bundle) {
    return i18nInstance.current.addResourceBundle(bundle);
  };
  i18nInstance.current.setLanguage(languageCode);
  if (defaultTranslations) {
    addResourceBundle(defaultTranslations);
  }
  if (translations) {
    addResourceBundle(translations);
  }
  if (defaultNs) {
    i18nInstance.current.setNamespace(defaultNs);
  }
  if (fallbackLanguageCode) {
    i18nInstance.current.setFallbackLanguages([fallbackLanguageCode]);
  }
  if (fallbackNs) {
    i18nInstance.current.setFallbackNamespaces(fallbackNs);
  }
  return /*#__PURE__*/_jsx(CrehanaI18nContext.Provider, {
    value: {
      t: t,
      addResourceBundle: addResourceBundle,
      languageCode: languageCode,
      countryCode: countryPrefix || countryCode
    },
    children: children
  });
};